import { FlightIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import React from 'react'
import BentoTile from './BentoTile'
import { FlightsSection, TileState } from './core'
import EmptyState from './EmptyState'
import SectionTeaser from './SectionTeaser'
import {
	AirportCode,
	FlightCode,
	FlightDashes,
	FlightMeta,
	FlightMetaCaption,
	FlightPath,
	FlightRow,
} from './FlightsTile.styles'
import { iconSizes } from '@/theme/tokens'

interface FlightsTileProps {
	section: FlightsSection
	/** Resolved tile state from the shared state machine (see `resolveTileState`). */
	state: TileState
	/** Navigate to the flights page. */
	onNavigate: () => void
	/** Render a skeleton while the flights query loads. */
	loading?: boolean
}

/**
 * The flights bento tile: the trip's first flight as big origin/destination
 * codes joined by a dashed path with a plane glyph, plus a right meta block
 * (flight code over "date · duration", each kept on one line).
 */
const FlightsTile: React.FC<FlightsTileProps> = ({ section, state, onNavigate, loading }) => {
	const icon = <FlightIcon sx={{ fontSize: iconSizes.sm }} />
	const { count, firstFlight } = section

	if (state === 'teaser') {
		return <SectionTeaser icon={icon} label="Flights" count={count} colSpan={5} loading={loading} />
	}

	const body =
		state === 'detail' && firstFlight ? (
			<FlightRow>
				<AirportCode>{firstFlight.originCode}</AirportCode>
				<FlightPath aria-hidden="true">
					<FlightDashes />
					<FlightIcon sx={{ fontSize: iconSizes.sm }} />
					<FlightDashes />
				</FlightPath>
				<AirportCode>{firstFlight.destinationCode}</AirportCode>
				<FlightMeta>
					<FlightCode>{firstFlight.flightCode}</FlightCode>
					{firstFlight.metaLabel && <FlightMetaCaption>{firstFlight.metaLabel}</FlightMetaCaption>}
				</FlightMeta>
			</FlightRow>
		) : (
			<EmptyState
				art="flights"
				text="Add a flight to share your travel details with the group."
			/>
		)

	return (
		<BentoTile
			icon={icon}
			label="Flights"
			count={count > 0 ? count : undefined}
			footer={count > 0 ? `All ${countLabel(count, 'flight')}` : 'See all flights'}
			ariaLabel="View flights"
			colSpan={5}
			onClick={onNavigate}
			loading={loading}
		>
			{body}
		</BentoTile>
	)
}

export default FlightsTile
